import axios from 'axios';
import { logger } from '../utils/logger';
import { installRequestId } from '../utils/requestId';
import { getMarketplaceApiBaseUrl, getMarketplaceDeployOrigin } from './resolveApiBaseUrl';

const ORDERS_API_URL = process.env.REACT_APP_ORDERS_API_URL ||
  getMarketplaceApiBaseUrl() || 'http://localhost:8002/api';

// Create axios instance with credentials to send cookies
const api = axios.create({
  baseURL: ORDERS_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});
installRequestId(api);

// Request interceptor - add Authorization header if needed
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Refresh token is in httpOnly cookie, sent automatically
        // Use same domain for token refresh to ensure cookies are sent
        const accountsApiUrl = process.env.REACT_APP_API_BASE_URL ||
          getMarketplaceDeployOrigin() || 'http://localhost:8001';
        await axios.post(
          `${accountsApiUrl}/api/token/refresh/`,
          {},  // No body needed - refresh token in cookie
          { withCredentials: true }  // Send cookies
        );
        // New tokens are set in httpOnly cookies by backend
        // Retry original request (cookies will be sent automatically)
        return api(originalRequest);
      } catch (refreshError) {
        // Only redirect to login if it's a persistent auth failure
        if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export const courierAPI = {
  // Get all orders assigned to courier
  getOrders: async () => {
    try {
      const response = await api.get('/courier/orders/');
      return response.data;
    } catch (error) {
      logger.error('Error fetching courier orders:', error);
      throw error;
    }
  },

  // Get order details
  getOrderDetails: async (orderId) => {
    try {
      const response = await api.get(`/courier/orders/${orderId}/`);
      return response.data;
    } catch (error) {
      logger.error(`Error fetching order ${orderId} details:`, error);
      throw error;
    }
  },

  // Mark all seller stops as picked up (single-seller / legacy)
  markPickup: async (orderId, description = '') => {
    try {
      const response = await api.post(`/courier/orders/${orderId}/pickup/`, {
        description
      });
      return response.data;
    } catch (error) {
      logger.error(`Error marking pickup for order ${orderId}:`, error);
      throw error;
    }
  },

  // Mark one seller stop as picked up
  markSellerPickup: async (orderId, sellerId, description = '') => {
    try {
      const response = await api.post(`/courier/orders/${orderId}/pickup/${sellerId}/`, {
        description
      });
      return response.data;
    } catch (error) {
      logger.error(`Error marking pickup for order ${orderId} seller ${sellerId}:`, error);
      throw error;
    }
  },

  // Report pickup problem at a specific seller stop
  reportSellerPickupProblem: async (orderId, sellerId, description) => {
    try {
      const response = await api.post(
        `/courier/orders/${orderId}/pickup/${sellerId}/problem/`,
        { description }
      );
      return response.data;
    } catch (error) {
      logger.error(`Error reporting pickup problem for order ${orderId} seller ${sellerId}:`, error);
      throw error;
    }
  },

  // Mark order as in transit
  markInTransit: async (orderId, description = '') => {
    try {
      const response = await api.post(`/courier/orders/${orderId}/in-transit/`, { description });
      return response.data;
    } catch (error) {
      logger.error(`Error marking in-transit for order ${orderId}:`, error);
      throw error;
    }
  },

  // Mark order out for delivery
  markOutForDelivery: async (orderId, description = '') => {
    try {
      const response = await api.post(`/courier/orders/${orderId}/out-for-delivery/`, { description });
      return response.data;
    } catch (error) {
      logger.error(`Error marking out-for-delivery for order ${orderId}:`, error);
      throw error;
    }
  },

  // Mark order as delivered
  markDelivered: async (orderId, description = '', lineDecisions = null) => {
    try {
      const payload = { description };
      if (lineDecisions && Object.keys(lineDecisions).length > 0) {
        payload.line_decisions = lineDecisions;
      }
      const response = await api.post(`/courier/orders/${orderId}/deliver/`, payload);
      return response.data;
    } catch (error) {
      logger.error(`Error marking delivery for order ${orderId}:`, error);
      throw error;
    }
  },

  // Mark order items as returned
  markReturned: async (orderId, description = '') => {
    try {
      const response = await api.post(`/courier/orders/${orderId}/return/`, {
        description
      });
      return response.data;
    } catch (error) {
      logger.error(`Error marking return for order ${orderId}:`, error);
      throw error;
    }
  },

  // Report a problem
  reportProblem: async (orderId, problemType, description) => {
    try {
      const response = await api.post(`/courier/orders/${orderId}/problem/`, {
        problem_type: problemType,
        description
      });
      return response.data;
    } catch (error) {
      logger.error(`Error reporting problem for order ${orderId}:`, error);
      throw error;
    }
  },
};

